import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Sparkles, ArrowLeft, TreePine, Home } from "lucide-react";
import BinauralPlayerStandalone from "../components/BinauralPlayerStandalone";
import { AppMenu } from "../components/AppMenu";
import { SiteFooter } from "../components/SiteFooter";

const INTERVENTION_TITLES = {
  quiet_the_worry: "Quiet The Worry",
  overcome_overwhelm: "Overcome Your Overwhelm",
  dial_down_tension: "Dial Down Body Tension",
  healing_old_stories: "Healing Old Stories To Let Them Go",
  beat_stage_fright: "Beat Stage Fright",
  fear_of_sleeplessness: "Fear Of Sleeplessness",
  shake_off_sadness: "Shake Off The Blues",
  boost_self_worth: "Boost Your Self-Worth",
  hold_boundaries: "Hold Your Boundaries",
  relief_from_grief: "Relief From Grief",
  calm_your_anger: "Calm Your Anger",
  enhance_performance: "Enhance Your Performance",
  new_habit_new_you: "New Habit, New You"
};

const NotImprovingPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Scroll to top when this page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="not-improving-page">
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/intervention/${type}`)}
                className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
              </button>
              <div>
                <p className="text-[#718096] text-sm">{INTERVENTION_TITLES[type] || type}</p>
                <h1 className="font-outfit font-medium text-[#1B2349]">Finding the Root</h1>
              </div>
            </div>
            <AppMenu />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-32 app-prose">
        <div className="relative bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 animate-fade-in-up overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#3090A8] via-[#78C0D8] to-[#E2B887] rounded-t-2xl" />

          <p className="text-xs font-medium text-[#3090A8] uppercase tracking-[0.14em] mb-2 pt-2">
            When a worry won't budge
          </p>
          <h2 className="font-outfit font-semibold text-[1.375rem] sm:text-2xl text-[#1B2349] leading-tight mb-4">
            Finding the root
          </h2>
          <p className="text-[#1B2349] leading-relaxed mb-6">
            You are a bit stuck, but you're doing great! Sometimes a worry just won't budge no matter how hard you work at it — and there's usually a good reason for that. Life has layers, and every now and then an old experience is quietly sitting behind the current one, making it hard to fully clear.
          </p>

          {/* Inline example — framed as a reflective quote so it reads softer than a paragraph */}
          <div className="pl-4 border-l-2 border-[#78C0D8]/40 mb-8">
            <p className="text-[#1B2349] leading-relaxed italic">
              For example, if public speaking worries you today, there may be an older memory underneath — a presentation in school that didn't go the way you hoped, or a childhood home that was loud and anxious, so that loud voices still make you jumpy. When that's the case, the older memory often needs a little attention first before the newer worry can fully let go.
            </p>
          </div>

          {/* Nature-metaphor takeaway */}
          <div className="flex items-start gap-4 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#E8F0E7] flex items-center justify-center flex-shrink-0">
              <TreePine className="w-5 h-5 text-[#3090A8]" />
            </div>
            <p className="text-[#1B2349] leading-relaxed">
              Think of it like a tree — sometimes you have to tend to the roots before the branches can produce fruit. See if there's an earlier experience connected to this one.
            </p>
          </div>

          {/* Steps — same numbered-beat pattern as task-1a */}
          <h3 className="font-outfit font-medium text-[1.0625rem] sm:text-base text-[#1B2349] mb-4">
            What to try next
          </h3>
          <ol className="space-y-4 mb-8">
            <li className="flex gap-4 items-start">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#E9F0E8] text-[#3090A8] flex items-center justify-center font-outfit font-semibold text-sm">1</span>
              <div>
                <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Give the previous step a few more passes</p>
                <p className="text-[#4A5568] leading-relaxed">Three rounds are usually enough. If you noticed any shift in mood, body sensation, or stress moving around in your body, stick with it a little longer. If you still don't sense movement, move to step 2 below.</p>
              </div>
            </li>
            <li className="flex gap-4 items-start">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#EAF0F9] text-[#78C0D8] flex items-center justify-center font-outfit font-semibold text-sm">2</span>
              <div>
                <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Let your mind drift back</p>
                <p className="text-[#4A5568] leading-relaxed">Start the bilateral sound below, then let your mind float back to an earlier time you felt this way. Keep your eyes moving or your hands tapping while the sound plays.</p>
              </div>
            </li>
            <li className="flex gap-4 items-start">
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#FEF3E7] text-[#B8965A] flex items-center justify-center font-outfit font-semibold text-sm">3</span>
              <div>
                <p className="font-outfit font-medium text-[#1B2349] mb-0.5">Target the older experience</p>
                <p className="text-[#4A5568] leading-relaxed">When you find a root experience, return to the menu, choose a focus that matches it, and start a fresh session there.</p>
              </div>
            </li>
          </ol>

          {/* Bilateral player for finding the root */}
          <BinauralPlayerStandalone />
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          <Button
            onClick={() => navigate(`/intervention/${type}`)}
            variant="outline"
            className="flex-1 rounded-full border-[#3090A8] text-[#3090A8] hover:bg-[#3090A8] hover:text-white"
            data-testid="back-to-intervention-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Practice
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            className="flex-1 btn-primary"
            data-testid="choose-new-target-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Return to Menu
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default NotImprovingPage;
